/**
 * Promise-based mutex for the Bluetooth printer port.
 *
 * The Star TSP100 SDK cannot handle two operations on `BT:TSP100` at once —
 * concurrent calls produce "Fail to Open Port". Every StarPRNT API call
 * (discovery, status check, print, cash drawer) must be serialized through
 * this lock.
 */

const LOCK_TIMEOUT_MS = 30_000;

let _chain: Promise<void> = Promise.resolve();
let _held = false;

function acquire(): Promise<() => void> {
  let release!: () => void;

  const prev = _chain;
  _chain = new Promise<void>((resolve) => {
    release = () => {
      _held = false;
      resolve();
    };
  });

  return prev.then(() => {
    _held = true;

    const timer = setTimeout(() => {
      console.warn('[PrinterLock] Lock held longer than 30 s — auto-releasing');
      release();
    }, LOCK_TIMEOUT_MS);

    const wrappedRelease = () => {
      clearTimeout(timer);
      release();
    };

    return wrappedRelease;
  });
}

/** Run `fn` with exclusive access to the printer port. Queues behind any pending operation. */
export async function withLock<T>(fn: () => Promise<T>): Promise<T> {
  const release = await acquire();
  try {
    return await fn();
  } finally {
    release();
  }
}

/** Non-blocking variant: runs `fn` if the lock is free, returns `null` if busy. */
export async function tryWithLock<T>(fn: () => Promise<T>): Promise<T | null> {
  if (_held) return null;
  return withLock(fn);
}

/** Whether an operation currently holds the lock (for UI display). */
export function isPrinterLocked(): boolean {
  return _held;
}
